/**
 * useRecentConversations Hook
 *
 * Manages recent conversation history in localStorage.
 * Stores up to MAX_RECENT conversations with title and timestamp.
 */

import { useCallback } from 'react';
import type { ConversationSummary } from '../types';
import { useLocalStorage } from './useLocalStorage';

const MAX_RECENT = 10;
const MAX_TITLE_LENGTH = 50;
const STORAGE_KEY = 'recent_conversations';

interface UseRecentConversationsReturn {
  conversations: ConversationSummary[];
  addOrUpdate: (chatId: string, title: string) => void;
  remove: (chatId: string) => void;
  clear: () => void;
}

/**
 * Truncate title to max length with ellipsis
 */
function truncateTitle(title: string): string {
  const trimmed = title.trim();
  if (trimmed.length <= MAX_TITLE_LENGTH) {
    return trimmed;
  }
  return trimmed.substring(0, MAX_TITLE_LENGTH - 3) + '...';
}

export function useRecentConversations(): UseRecentConversationsReturn {
  const [conversations, setConversations] = useLocalStorage<
    ConversationSummary[]
  >(STORAGE_KEY, []);

  /**
   * Add or update a conversation in the recent list.
   * If it exists, moves it to the front with updated timestamp.
   * If new, adds it to the front and trims to MAX_RECENT.
   */
  const addOrUpdate = useCallback(
    (chatId: string, title: string) => {
      setConversations((prev) => {
        // Remove existing entry if present
        const filtered = prev.filter((c) => c.chatId !== chatId);

        // Create new entry at front
        const newConversation: ConversationSummary = {
          chatId,
          title: truncateTitle(title),
          timestamp: Date.now(),
        };

        // Add to front and limit to MAX_RECENT
        return [newConversation, ...filtered].slice(0, MAX_RECENT);
      });
    },
    [setConversations],
  );

  /**
   * Remove a conversation from the recent list
   */
  const remove = useCallback(
    (chatId: string) => {
      setConversations((prev) => prev.filter((c) => c.chatId !== chatId));
    },
    [setConversations],
  );

  /**
   * Clear all recent conversations
   */
  const clear = useCallback(() => {
    setConversations([]);
  }, [setConversations]);

  return {
    conversations,
    addOrUpdate,
    remove,
    clear,
  };
}
